import { HTTPError } from "@/Utils/request/types";

interface SessionUserOptions<User extends { id: string }> {
  signal: AbortSignal;
  getAccessToken(): string | null;
  readUser(access: string, signal: AbortSignal): Promise<User>;
  refresh(): Promise<void>;
  expectedOwnerId?: string;
}

/** At most two identity reads and one shared refresh; never replays a write. */
export async function loadSessionUser<User extends { id: string }>({
  signal,
  getAccessToken,
  readUser,
  refresh,
  expectedOwnerId,
}: SessionUserOptions<User>): Promise<User> {
  const access = getAccessToken();
  if (!access) throw new Error("Session changed before verification");
  const verify = async (token: string) => {
    signal.throwIfAborted();
    const user = await readUser(
      token,
      AbortSignal.any([signal, AbortSignal.timeout(15000)]),
    );
    signal.throwIfAborted();
    if (getAccessToken() !== token)
      throw new Error("Session changed during verification");
    if (!user.id || (expectedOwnerId && user.id !== expectedOwnerId))
      throw new Error("Session owner changed");
    return user;
  };
  try {
    return await verify(access);
  } catch (error) {
    signal.throwIfAborted();
    if (!(error instanceof HTTPError) || error.status !== 401) throw error;
    // An owned editor uses its explicit recovery panel. Cold starts may refresh.
    if (expectedOwnerId) throw error;
    if (getAccessToken() === access) {
      try {
        await refresh();
      } catch (refreshError) {
        // A concurrent login/rotation wins over an obsolete refresh failure.
        if (getAccessToken() === access) throw refreshError;
      }
    }
    signal.throwIfAborted();
    const currentAccess = getAccessToken();
    if (!currentAccess) throw new Error("Session ended during verification");
    return verify(currentAccess);
  }
}
